/* eslint-disable no-magic-numbers */
import {
  DIV_POOL1,
  DIV_POOL1_ANSW,
  DIV_POOL2,
  DIV_POOL2_ANSW,
  MUL_POOL1,
  MUL_POOL1_ANSW,
  MUL_POOL2,
  MUL_POOL2_ANSW,
  ROUND3_DIV_POOL,
  ROUND3_DIV_POOL_ANSW,
  ROUND3_MUL_POOL,
  ROUND3_MUL_POOL_ANSW,
  mulDivTableRef,
} from '../../games/data/vifMulDivData';
import { buildProblemSet } from '../../games/engine/ProblemSource';
import { pickDistinctIndices } from '../../games/engine/boardMath';

import type { ProblemPool } from './deal';

/**
 * Problem-pool selection for the 4x4 block mul/div board (`VIFMulDiv`,
 * gameFlag 3=mul, 4=div). Direct port of `VIFMulDivGame.populateProblem`'s
 * MUL/DIV branches (`games/VIFMulDiv.ts`) - the `else` branch there (neither
 * MUL nor DIV) is dead code for this composition and is not ported.
 */

export type BlockMulDivFlag = 3 | 4;

const MUL: BlockMulDivFlag = 3;

/**
 * Mutable pool-selection memory for one game. `useRulesBoardGame` recreates it
 * at every new game, and a block game has a fixed round and a single layer, so
 * anything held here means "already drawn during this game".
 */
export interface BlockMulDivProblemState {
  /**
   * Round 4's random draw, kept so a retry after a wrong answer or a timeout
   * deals the same problems. Legacy `blnFirstTimeLayer` guard
   * (`games/VIFMulDiv.ts`, `fillFromPoolsRound4`).
   */
  round4Pool: ProblemPool | null;
}

export function createBlockMulDivProblemState(): BlockMulDivProblemState {
  return { round4Pool: null };
}

export interface PopulateArgs {
  flag: BlockMulDivFlag;
  /** Legacy `intLayer`, 1-based. */
  layer: number;
  /** Legacy `gameFrame.round`, 1-based. */
  round: number;
  /** Unused by mul/div; accepted for shape compatibility with `PopulateProblemsArgs`. */
  columns: number;
  state: BlockMulDivProblemState;
  rng?: () => number;
}

/** `count` distinct entries from a pool, paired with their answers. */
function drawDistinct(
  eqs: readonly string[],
  answers: readonly string[],
  count: number,
  rng: () => number,
): ProblemPool {
  const picked = pickDistinctIndices(eqs.length, count, rng);
  return {
    topEQs: picked.map((index) => eqs[index]!),
    bottomAnswers: picked.map((index) => answers[index]!),
  };
}

function concat(first: ProblemPool, second: ProblemPool): ProblemPool {
  return {
    topEQs: [...first.topEQs, ...second.topEQs],
    bottomAnswers: [...first.bottomAnswers, ...second.bottomAnswers],
  };
}

/**
 * Returns the pool for the given flag/layer/round. Rounds 1-2: the fixed
 * 16-entry table. Round 3: the fixed 7-entry table plus 9 random extras from
 * the round-3 pool. Round 4: 9 distinct from a 36-entry pool1 plus 7 distinct
 * from a 28-entry pool2, held for the rest of the game.
 */
export function populateBlockMulDivProblems({
  flag,
  layer,
  round,
  state,
  rng = Math.random,
}: PopulateArgs): ProblemPool {
  if (round === 4) {
    return (state.round4Pool ??=
      flag === MUL
        ? concat(
            drawDistinct(MUL_POOL1, MUL_POOL1_ANSW, 9, rng),
            drawDistinct(MUL_POOL2, MUL_POOL2_ANSW, 7, rng),
          )
        : concat(
            drawDistinct(DIV_POOL1, DIV_POOL1_ANSW, 9, rng),
            drawDistinct(DIV_POOL2, DIV_POOL2_ANSW, 7, rng),
          ));
  }

  const table = buildProblemSet({
    kind: 'table',
    ref: mulDivTableRef(flag === MUL ? 'mul' : 'div', round, layer),
  });

  if (round !== 3) {
    return table;
  }

  return flag === MUL
    ? concat(table, drawDistinct(ROUND3_MUL_POOL, ROUND3_MUL_POOL_ANSW, 9, rng))
    : concat(
        table,
        drawDistinct(ROUND3_DIV_POOL, ROUND3_DIV_POOL_ANSW, 9, rng),
      );
}
